import { useRef, useState } from 'react'
import { ORGANIZATION_FORM_CURRENCIES } from '../../utils/currencies'
import { COUNTRIES } from '../../utils/countries'
import { slugify } from '../../utils/slugify'
import { emptyOrganizationForm } from '../../utils/defaultOrganizationForm'
import { isValidPhone } from '../../utils/phone'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { FeatureToggle } from '../../components/ui/FeatureToggle'
import { ImageCropDialog } from '../../components/ui/ImageCropDialog'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { UploadProgressBar } from '../../components/ui/UploadProgressBar'
import { organizationsApi } from '../../services/organizationsApi'
import { cn } from '../../lib/utils'
import { getOrgDashboardLoginUrl } from '../../config/platformUrls'
import { STANDARD_ADDRESS_PLACEHOLDER } from '../../lib/formatStandardAddress'
import { orgStatusDescription } from '../../utils/orgStatus'

type FormErrors = Record<string, string>

const featureMeta = [
  { key: 'orders', label: 'Orders', description: 'Track and fulfil customer orders' },
  { key: 'inventory', label: 'Inventory', description: 'Stock levels and storage' },
  { key: 'analytics', label: 'Analytics', description: 'Sales figures and reports' },
  { key: 'marketing', label: 'Marketing', description: 'Campaigns and customer messages' },
  { key: 'payments', label: 'Payments', description: 'Taking payments and paying out' },
]

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const SUBDOMAIN_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const ALLOWED_CODES = new Set(ORGANIZATION_FORM_CURRENCIES.map((c) => c.code))
const DEFAULT_LOGO_URL = 'https://placehold.co/120x40/e2e8f0/64748b?text=Logo'

function deepCloneForm(obj) {
  return JSON.parse(JSON.stringify(obj))
}

export function OrganizationForm({
  mode = 'create',
  initialValues = null,
  onSubmit,
  onCancel,
  submitting = false,
  serverErrors = null,
}) {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [form, setForm] = useState(() => {
    const base = deepCloneForm(initialValues || emptyOrganizationForm())
    if (!initialValues) {
      base.storefront.logoUrl = ''
    }
    return base
  })
  const [autoSubdomain, setAutoSubdomain] = useState(mode === 'create')
  const [errors, setErrors] = useState<FormErrors>({})
  const mergedErrors = { ...errors, ...(serverErrors ?? {}) }
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadMessage, setUploadMessage] = useState('')
  const [logoCrop, setLogoCrop] = useState<{ url: string; mime: string } | null>(null)

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function setStorefront(key, value) {
    setForm((f) => {
      const nextSf = { ...f.storefront, [key]: value }
      if (key === 'storeName' && autoSubdomain && mode === 'create') {
        nextSf.subdomain = slugify(value)
      }
      return { ...f, storefront: nextSf }
    })
  }

  function setFeature(key, value) {
    setForm((f) => ({
      ...f,
      features: { ...f.features, [key]: value },
    }))
  }

  function setOwnerField(key, value) {
    setForm((f) => ({
      ...f,
      owner: { ...(f.owner || { firstName: '', lastName: '', email: '' }), [key]: value },
    }))
  }

  function closeLogoCrop() {
    if (logoCrop?.url) URL.revokeObjectURL(logoCrop.url)
    setLogoCrop(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function handleLogoFile(ev) {
    const file = ev.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({ ...prev, logo: 'Please choose an image file (PNG, JPG, or WebP).' }))
      ev.target.value = ''
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: 'Image must be 2MB or smaller.' }))
      ev.target.value = ''
      return
    }
    setErrors((prev) => {
      const next = { ...prev }
      delete next.logo
      return next
    })
    setUploadMessage('')
    if (logoCrop?.url) URL.revokeObjectURL(logoCrop.url)
    setLogoCrop({ url: URL.createObjectURL(file), mime: file.type })
  }

  async function uploadCroppedLogo(file: File) {
    closeLogoCrop()
    setUploadingLogo(true)
    setUploadProgress(0)
    setUploadMessage('')
    try {
      const uploaded = await organizationsApi.uploadLogo(file, setUploadProgress)
      setStorefront('logoUrl', uploaded.url || '')
      setUploadMessage('Upload complete')
      setErrors((prev) => {
        const next = { ...prev }
        delete next.logo
        return next
      })
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        logo: error?.message || 'Failed to upload image. Please try again.',
      }))
      setUploadMessage('')
    } finally {
      setUploadingLogo(false)
      setUploadProgress(0)
    }
  }

  function validate() {
    const e: FormErrors = {}
    const name = form.name?.trim() || ''
    const country = form.country?.trim() || ''
    const ofn = (form.owner?.firstName ?? '').trim()
    const oln = (form.owner?.lastName ?? '').trim()
    const oemail = (form.owner?.email ?? '').trim()
    const subdomain = (form.storefront?.subdomain ?? '').trim().toLowerCase()
    const storeContactEmail = (form.storefront?.contactEmail ?? '').trim()

    if (!name) e.name = 'Organization name is required.'
    const phone = (form.phone ?? '').trim()
    if (!phone) e.phone = 'Phone number is required so we can always reach the business.'
    else if (!isValidPhone(phone)) e.phone = 'Enter a valid phone number with the country code, e.g. +49 176 12345678.'
    if (!country) e.country = 'Select a country.'
    if (!form.currency) e.currency = 'Select a currency.'
    else if (!ALLOWED_CODES.has(form.currency)) e.currency = 'Choose a supported currency.'

    if (mode === 'create') {
      if (!ofn || !oln) e.ownerName = 'First and last name are required.'
      if (!oemail) e.ownerEmail = 'Email is required.'
      else if (!EMAIL_RE.test(oemail)) e.ownerEmail = 'Enter a valid email address.'
      if (!subdomain) e.subdomain = 'Shop web address is required.'
      else if (!SUBDOMAIN_RE.test(subdomain)) {
        e.subdomain = 'Use lowercase letters, numbers, and hyphens only.'
      }
    } else {
      if ((ofn && !oln) || (!ofn && oln)) {
        e.ownerName = 'Enter both first name and last name, or leave both empty.'
      }
      if (oemail && !EMAIL_RE.test(oemail)) e.ownerEmail = 'Enter a valid email address.'
      if (subdomain && !SUBDOMAIN_RE.test(subdomain)) {
        e.subdomain = 'Use lowercase letters, numbers, and hyphens only.'
      }
    }

    if (storeContactEmail && !EMAIL_RE.test(storeContactEmail)) {
      e.storeContactEmail = 'Enter a valid shop email.'
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  function handleSubmit(ev) {
    ev.preventDefault()
    if (!validate()) return

    const ofn = (form.owner?.firstName ?? '').trim()
    const oln = (form.owner?.lastName ?? '').trim()
    const oemail = (form.owner?.email ?? '').trim().toLowerCase()
    const dashboardUrl = mode === 'create' ? getOrgDashboardLoginUrl() : ''

    const payload = {
      name: form.name.trim(),
      country: form.country.trim(),
      phone: (form.phone ?? '').trim(),
      currency: form.currency,
      status: form.status,
      businessDescription: (form.businessDescription ?? '').trim(),
      contactStreet: (form.contactStreet ?? '').trim(),
      contactPostcode: (form.contactPostcode ?? '').trim(),
      contactRegion: (form.contactRegion ?? '').trim(),
      contactCountry: (form.contactCountry ?? '').trim(),
      storefront: {
        storeName: (form.storefront.storeName || '').trim() || form.name.trim(),
        subdomain: (form.storefront.subdomain || '').trim().toLowerCase(),
        logoUrl: (form.storefront.logoUrl || '').trim() || DEFAULT_LOGO_URL,
        themePrimary: form.storefront.themePrimary || '#3b82f6',
        themeSecondary: form.storefront.themeSecondary || '#64748b',
        contactEmail: (form.storefront.contactEmail || '').trim().toLowerCase(),
        contactPhone: (form.storefront.contactPhone || '').trim(),
        sellingSummary: (form.storefront.sellingSummary || '').trim(),
      },
      features: { ...form.features },
      ...(ofn && oln
        ? {
            owner: {
              firstName: ofn,
              lastName: oln,
              ...(oemail ? { email: oemail } : {}),
            },
          }
        : {}),
      ...(dashboardUrl ? { orgDashboardLoginUrl: dashboardUrl } : {}),
    }
    onSubmit(payload)
  }

  const currencyOptions = ORGANIZATION_FORM_CURRENCIES.map((c) => (
    <option key={c.code} value={c.code}>
      {c.label}
    </option>
  ))

  const shopTitleName = (form.storefront?.storeName || form.name || '').trim()
  const shopSectionTitle = shopTitleName
    ? `${/s$/i.test(shopTitleName) ? `${shopTitleName}'` : `${shopTitleName}'s`} online shop`
    : mode === 'create'
      ? 'Their online shop'
      : 'Online shop'

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <Card>
        <CardHeader
          title="Organization"
          description="The company behind the shop — name, country, and usual currency."
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            id="name"
            label="Company name"
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            error={mergedErrors.name}
            autoComplete="organization"
            className="sm:col-span-2"
          />
          <Select
            id="country"
            label="Country"
            value={form.country}
            onChange={(e) => setField('country', e.target.value)}
            error={mergedErrors.country}
          >
            <option value="">Select country</option>
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            id="currency-org"
            label="Usual currency"
            value={form.currency}
            onChange={(e) => setField('currency', e.target.value)}
            error={mergedErrors.currency}
          >
            {currencyOptions}
          </Select>
          <p className="-mt-3 text-xs text-muted-foreground sm:col-span-2">
            Used for new shops. You can still pick a different currency for a shop later.
          </p>
          <Select
            id="status"
            label="Status"
            value={form.status}
            onChange={(e) => setField('status', e.target.value)}
            className="sm:col-span-2"
          >
            <option value="inactive">Inactive (setup)</option>
            <option value="active">Active</option>
            {mode === 'edit' ? <option value="suspended">Suspended</option> : null}
          </Select>
          {form.status ? (
            <p className="sm:col-span-2 text-xs text-muted-foreground">
              {orgStatusDescription(form.status)}
            </p>
          ) : null}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="About the company"
          description="Optional — a short note about the company for our team."
        />
        <div>
          <label htmlFor="businessDescription" className="mb-1.5 block text-sm font-medium text-foreground">
            About the company
          </label>
          <textarea
            id="businessDescription"
            rows={4}
            disabled={submitting}
            value={form.businessDescription ?? ''}
            onChange={(e) => setField('businessDescription', e.target.value)}
            className={cn(
              'min-h-[5.5rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60',
            )}
            placeholder="e.g. Family-run fashion brand based in Lagos"
          />
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Main contact"
          description="Who we should reach, and who signs in to manage the shop."
        />
        <div className="grid gap-5 sm:grid-cols-2">
          <Input
            id="ownerFirstName"
            label="First name"
            value={form.owner?.firstName ?? ''}
            onChange={(e) => setOwnerField('firstName', e.target.value)}
            error={mergedErrors.ownerName}
            autoComplete="given-name"
          />
          <Input
            id="ownerLastName"
            label="Last name"
            value={form.owner?.lastName ?? ''}
            onChange={(e) => setOwnerField('lastName', e.target.value)}
            autoComplete="family-name"
          />
          <Input
            id="ownerEmail"
            type="email"
            label="Email (for signing in)"
            value={form.owner?.email ?? ''}
            onChange={(e) => setOwnerField('email', e.target.value)}
            error={mergedErrors.ownerEmail}
            autoComplete="email"
            className="sm:col-span-2"
          />
          <Input
            id="ownerPhone"
            type="tel"
            label="Phone number (contact person)"
            placeholder="+49 176 12345678"
            value={form.phone ?? ''}
            onChange={(e) => setField('phone', e.target.value)}
            error={mergedErrors.phone}
            autoComplete="tel"
            className="sm:col-span-2"
          />
          <Input
            id="contactStreet"
            label="Street name/No"
            value={form.contactStreet ?? ''}
            onChange={(e) => setField('contactStreet', e.target.value)}
            autoComplete="street-address"
          />
          <Input
            id="contactPostcode"
            label="Postcode"
            value={form.contactPostcode ?? ''}
            onChange={(e) => setField('contactPostcode', e.target.value)}
            autoComplete="postal-code"
          />
          <Input
            id="contactRegion"
            label="Region"
            value={form.contactRegion ?? ''}
            onChange={(e) => setField('contactRegion', e.target.value)}
            autoComplete="address-level1"
          />
          <Select
            id="contactCountry"
            label="Contact country"
            value={form.contactCountry ?? ''}
            onChange={(e) => setField('contactCountry', e.target.value)}
          >
            <option value="">Optional — select country</option>
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <p className="sm:col-span-2 text-xs text-muted-foreground">
            Format: {STANDARD_ADDRESS_PLACEHOLDER}
          </p>
        </div>
      </Card>

      <Card className="border-primary/20 ring-1 ring-primary/15">
        <CardHeader
          title={shopSectionTitle}
          description="Name, web address, how customers reach them, what they sell, and look."
        />

        <div className="space-y-6">
          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              id="storeName"
              label="Shop name"
              value={form.storefront.storeName}
              onChange={(e) => setStorefront('storeName', e.target.value)}
              placeholder="Shown on their online shop"
              className="sm:col-span-2"
            />
            <div className="sm:col-span-2">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label htmlFor="storeSubdomain" className="text-sm font-medium text-foreground">
                  Shop web address
                </label>
                {mode === 'create' ? (
                  <label className="flex cursor-pointer items-center gap-1.5 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={autoSubdomain}
                      onChange={(e) => {
                        const on = e.target.checked
                        setAutoSubdomain(on)
                        if (on) {
                          setStorefront('subdomain', slugify(form.storefront.storeName || form.name))
                        }
                      }}
                      className="rounded border-border"
                    />
                    Fill from shop name
                  </label>
                ) : null}
              </div>
              <input
                id="storeSubdomain"
                disabled={autoSubdomain && mode === 'create'}
                className="w-full rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:opacity-60"
                value={form.storefront.subdomain ?? ''}
                onChange={(e) => {
                  setAutoSubdomain(false)
                  setStorefront('subdomain', e.target.value.toLowerCase())
                }}
                placeholder="acme-store"
              />
              {mergedErrors.subdomain ? (
                <p className="mt-1 text-xs text-destructive">{mergedErrors.subdomain}</p>
              ) : (
                <p className="mt-1 text-xs text-muted-foreground">
                  Short name used in the shop link (lowercase letters, numbers, and hyphens).
                </p>
              )}
            </div>

            <Input
              id="storeContactEmail"
              type="email"
              label="Shop email (shown to customers)"
              value={form.storefront.contactEmail ?? ''}
              onChange={(e) => setStorefront('contactEmail', e.target.value)}
              error={mergedErrors.storeContactEmail}
              placeholder="hello@theirshop.com"
            />
            <Input
              id="storeContactPhone"
              type="tel"
              label="Shop phone (shown to customers)"
              value={form.storefront.contactPhone ?? ''}
              onChange={(e) => setStorefront('contactPhone', e.target.value)}
              placeholder="+234 …"
            />

            <div className="sm:col-span-2">
              <label htmlFor="sellingSummary" className="mb-1.5 block text-sm font-medium text-foreground">
                What this shop sells
              </label>
              <textarea
                id="sellingSummary"
                rows={3}
                disabled={submitting}
                value={form.storefront.sellingSummary ?? ''}
                onChange={(e) => setStorefront('sellingSummary', e.target.value)}
                className="min-h-[4.5rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60"
                placeholder="e.g. Ready-to-wear dresses and custom sewing for women"
              />
            </div>

            <div className="sm:col-span-2">
              <span className="mb-1.5 block text-sm font-medium text-foreground">Store logo</span>
              <p className="mb-3 text-xs text-muted-foreground">
                Crop to a tight square — remove empty margins so the mark fills the header. We upload the cropped
                image and save the link for you.
              </p>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <div className="flex shrink-0 flex-col gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleLogoFile}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo || submitting || logoCrop != null}
                    className="inline-flex cursor-pointer items-center justify-center rounded-2xl border border-border bg-card px-4 py-2.5 text-sm font-medium text-foreground shadow-sm transition-all hover:bg-muted hover:shadow-md disabled:cursor-not-allowed disabled:opacity-45"
                  >
                    {uploadingLogo ? 'Uploading…' : 'Upload image'}
                  </button>
                  <button
                    type="button"
                    disabled={uploadingLogo || submitting}
                    className="cursor-pointer text-xs font-medium text-primary hover:text-primary/80"
                    onClick={() => {
                      setStorefront('logoUrl', '')
                      setUploadMessage('')
                      setErrors((prev) => {
                        const next = { ...prev }
                        delete next.logo
                        return next
                      })
                    }}
                  >
                    Remove logo
                  </button>
                </div>
                {(uploadingLogo || form.storefront.logoUrl) && (
                  <div className="min-h-[4.5rem] flex-1 rounded-2xl border border-dashed border-border bg-muted/50 p-4">
                    {uploadingLogo ? (
                      <UploadProgressBar
                        active={uploadingLogo}
                        label="Uploading logo…"
                        progress={uploadProgress}
                      />
                    ) : null}
                    {form.storefront.logoUrl ? (
                      <img
                        src={form.storefront.logoUrl}
                        alt="Logo preview"
                        className="max-h-20 max-w-full object-contain object-left"
                      />
                    ) : null}
                  </div>
                )}
              </div>
              {uploadMessage ? (
                <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">{uploadMessage}</p>
              ) : null}
              {mergedErrors.logo ? <p className="mt-1 text-xs text-destructive">{mergedErrors.logo}</p> : null}
            </div>

            <Input
              id="themePrimary"
              type="color"
              label="Primary theme color"
              inputClassName="h-11 min-h-[2.75rem] py-1"
              value={form.storefront.themePrimary}
              onChange={(e) => setStorefront('themePrimary', e.target.value)}
            />
            <Input
              id="themeSecondary"
              type="color"
              label="Secondary theme color"
              inputClassName="h-11 min-h-[2.75rem] py-1"
              value={form.storefront.themeSecondary}
              onChange={(e) => setStorefront('themeSecondary', e.target.value)}
            />
          </div>

          <div>
            <h3 className="mb-3 text-sm font-semibold text-foreground">Product areas</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {featureMeta.map((f) => (
                <FeatureToggle
                  key={f.key}
                  label={f.label}
                  description={f.description}
                  checked={Boolean(form.features[f.key])}
                  onChange={(v) => setFeature(f.key, v)}
                  disabled={submitting}
                />
              ))}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        {onCancel ? (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </Button>
        ) : null}
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : mode === 'create' ? 'Create organization' : 'Save changes'}
        </Button>
      </div>

      <ImageCropDialog
        open={logoCrop != null}
        imageUrl={logoCrop?.url ?? null}
        sourceMimeType={logoCrop?.mime}
        aspect={1}
        title="Crop logo"
        description="Drag and zoom so the mark fills the square. Empty margins will be removed."
        preferPng
        maxEdge={1024}
        fileName="store-logo"
        onCancel={closeLogoCrop}
        onConfirm={(file) => void uploadCroppedLogo(file)}
      />
    </form>
  )
}
